import { useEffect, useMemo, useRef, useState } from 'react'
import L from 'leaflet'
import {
  LayersControl,
  MapContainer,
  Marker,
  TileLayer,
  useMap,
} from 'react-leaflet'
import 'leaflet/dist/leaflet.css'

// Default Leaflet marker icon (Webpack/Vite-friendly CDN fallback)
const defaultIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
})

interface DeviceLocationPickerProps {
  lat: number | null
  long: number | null
  onChange: (lat: number, long: number) => void
  height?: string
}

/**
 * Syncs map view with external lat/long prop changes (e.g. user types in the input).
 */
const RecenterMap = ({ lat, long }: { lat: number; long: number }) => {
  const map = useMap()
  useEffect(() => {
    map.setView([lat, long], map.getZoom())
  }, [lat, long, map])
  return null
}

export const DeviceLocationPicker = ({
  lat,
  long,
  onChange,
  height = '400px',
}: DeviceLocationPickerProps) => {
  // Use provided coordinates or fall back to a reasonable default (world view of NZ)
  const initialCenter = useMemo<[number, number]>(() => {
    if (
      lat !== null &&
      long !== null &&
      !Number.isNaN(lat) &&
      !Number.isNaN(long) &&
      (lat !== 0 || long !== 0)
    ) {
      return [lat, long]
    }
    return [-41.2865, 174.7762] // Wellington, NZ — safe default
  }, [])
  // Intentionally only computed on first mount; subsequent updates are handled by RecenterMap

  const hasCoords = lat !== null && long !== null && !(lat === 0 && long === 0)
  const markerRef = useRef<L.Marker>(null)
  const [markerPos, setMarkerPos] = useState<[number, number]>(
    hasCoords ? [lat as number, long as number] : initialCenter
  )

  // Sync marker position when incoming lat/long props change
  useEffect(() => {
    if (lat !== null && long !== null && !Number.isNaN(lat) && !Number.isNaN(long)) {
      setMarkerPos([lat, long])
    }
  }, [lat, long])

  return (
    <div className="w-full rounded-md overflow-hidden border" style={{ height }}>
      <MapContainer
        center={initialCenter}
        zoom={hasCoords ? 17 : 5}
        style={{ height: '100%', width: '100%' }}
      >
        <LayersControl position="topright">
          <LayersControl.BaseLayer checked name="Satellite">
            <TileLayer
              attribution="&copy; Esri"
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              maxZoom={19}
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Streets">
            <TileLayer
              attribution="&copy; OpenStreetMap contributors"
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              maxZoom={19}
            />
          </LayersControl.BaseLayer>
        </LayersControl>

        <Marker
          position={markerPos}
          draggable
          icon={defaultIcon}
          ref={markerRef}
          eventHandlers={{
            dragend: () => {
              const marker = markerRef.current
              if (!marker) return
              const pos = marker.getLatLng()
              const newLat = Number(pos.lat.toFixed(6))
              const newLng = Number(pos.lng.toFixed(6))
              setMarkerPos([newLat, newLng])
              onChange(newLat, newLng)
            },
          }}
        />

        {hasCoords && <RecenterMap lat={lat as number} long={long as number} />}
      </MapContainer>
    </div>
  )
}
